import { Activity, AlertCircle, Home } from 'lucide-react';
import { Equipment, OccupancyType, Project } from '../types/project';
import { isDemoProjectId } from '../lib/demoProject';
import { useUtilityDetail } from '../hooks/useUtility';
import { isUsableTemplate } from '../lib/requestForm';
import { routesFor } from '../lib/utilityRoutes';
import { existingLoads, removedLoads } from '../lib/calc/loadRoles';
import { checkTraditionalEligibility } from '../lib/calc/eligibility';
import { canRunTraditional } from '../lib/calc/runTraditional';
import { canRunMeasured, hasTraditionalEntries, TRADITIONAL_INPUT_RESET } from '../lib/calc/reportBasis';
import { measuredMethodologyVersion, traditionalMethodologyVersion } from '../lib/calc/citations';
import {
  defaultEditionForState,
  EDITION_PACKS,
  EditionKey,
  editionSelectionToStore,
  getEditionPack,
} from '../lib/calc/editions';
import { AMIDataInput } from './AMIDataInput';
import { DwellingProfile } from './DwellingProfile';
import { Tooltip } from './Tooltip';

const DWELLING_UNIT_TIP =
  'A dwelling unit requires permanent provisions for cooking and sanitation — an accessory unit without a kitchen is not a dwelling unit.';

const OCCUPANCY_OPTIONS: Array<{ value: OccupancyType; label: string; tooltip?: string }> = [
  { value: 'one-family', label: 'Single-family home', tooltip: DWELLING_UNIT_TIP },
  {
    value: 'dwelling-unit',
    label: 'One unit within a two-family or apartment building',
    tooltip: DWELLING_UNIT_TIP,
  },
  { value: 'multifamily-building', label: 'Whole apartment or condo building service' },
  { value: 'non-dwelling', label: 'Commercial or other non-dwelling' },
];

// The Existing Load step (docs/specs/traditional-calcs.md §5.1–§5.2): both
// calculation paths answer the same question — what is the existing load? —
// so they fork inside one step, not into two tools. Measured mode is the
// AMIDataInput experience unchanged; traditional mode is the Dwelling
// Profile. Neither blocks the other, nothing new is required on the measured
// path, and the recommendation only steers.

interface ExistingLoadStepProps {
  projectId: string | null;
  project: Partial<Project>;
  amiDataCount: number;
  amiData: Array<{ timestamp: string; kw: number }>;
  equipment: Equipment[];
  onChange: (updates: Partial<Project>) => void;
  onUploadCSV: (data: Array<{ timestamp: string; kw: number }>) => void;
  onDeleteData: () => void;
  onAddExisting: (seed: Partial<Equipment>) => void;
  onSplitExisting: (id: string) => void;
  /** Moves an inventory row to the removed list (load_role 'removed'). */
  onMarkRemoved?: (id: string) => void;
  /** Moves a removed row back into the inventory. */
  onReturnToInventory?: (id: string) => void;
  onNavigate?: (tab: 'project' | 'service' | 'equipment' | 'ami' | 'results' | 'help') => void;
  onUpdateEquipment: (id: string, updates: Partial<Equipment>) => void;
  onDeleteEquipment: (id: string) => void;
  isReadOnly?: boolean;
  showRequiredHighlights?: boolean;
  subscriptionStatus?: 'active' | 'canceled' | 'past_due' | null;
  /** The paywall as Results sees it: the peak's value shows only when true. */
  unlocked?: boolean;
  onRelockTriggered?: () => void;
  /** Re-read the project and its readings from the cloud (homeowner upload landed). */
  onReloadProject?: () => void | Promise<void>;
  /** The open sub-path, held by App so leaving the tab and returning does not
   * forget an explicit choice. Null = not chosen this session; the step
   * derives a sensible initial mode. */
  mode: 'measured' | 'traditional' | null;
  onModeChange: (mode: 'measured' | 'traditional') => void;
}

type Mode = 'measured' | 'traditional' | null;

function deriveInitialMode(
  project: Partial<Project>,
  amiDataCount: number,
  existingRowCount: number
): Mode {
  // The guided demo gets the same chooser a contractor does: its fork beat
  // waits for a card to be clicked. (An earlier version forced it onto the
  // measured path here, from when the demo only knew one.)
  // A stored report basis is an explicit choice — reopen where the work is.
  if (project.report_method === 'traditional') return 'traditional';
  if (project.report_method === 'measured') return 'measured';
  if (amiDataCount > 0 || (project.max_recorded_demand_kw ?? 0) > 0) return 'measured';
  if (
    project.dwelling_floor_area_sqft != null ||
    existingRowCount > 0 ||
    project.inventory_attested ||
    project.occupancy_type != null
  ) {
    return 'traditional';
  }
  return null;
}

export function ExistingLoadStep(props: ExistingLoadStepProps) {
  const {
    projectId,
    project,
    amiDataCount,
    equipment,
    onChange,
    onAddExisting,
    onSplitExisting,
    onMarkRemoved,
    onReturnToInventory,
    onUpdateEquipment,
    onDeleteEquipment,
    isReadOnly = false,
  } = props;

  const existingRows = existingLoads(equipment);

  // Section numbers are edition-dependent — 2026 moved every load-calculation
  // rule from Article 220 into Article 120 — so no literal ever ships in this
  // component. The mode switch carries no citation at all: a tab is a place in
  // the app, not a code reference, and a stale number there reads as a
  // calculation written against the wrong code. The chooser cards keep one
  // citation each, resolved through the project's edition pack.
  // The state default is resolved on every render and never written into
  // the project. An earlier version seeded code_edition on mount "so the
  // stored project and the displayed pack cannot disagree" — but a project
  // whose Existing Load tab was opened before its state was entered got
  // '2023' stamped in, and the stamp then outranked the state for good.
  // Every reader resolves null the same way (editions.ts), so nothing can
  // disagree without a stored value either.
  const stateCode = (project.customer_state || '').trim().toUpperCase();
  const stateDefault = defaultEditionForState(stateCode);
  const pack = getEditionPack(project.code_edition ?? stateDefault);
  const measuredRef = pack.sectionMap.determiningExisting;
  const traditionalRef = pack.sectionMap.existingDwelling;

  // Controlled by App: an explicit choice survives tab switches. Derivation
  // only fills in when nothing has been chosen this session.
  const mode: Mode =
    props.mode ?? deriveInitialMode(project, amiDataCount, existingRows.length);
  // The chosen utility's row decides the recommendation. A project whose
  // contractor chose "Other (not listed)" has no row but a typed provider
  // name, and gets the generic self-download route.
  const { utility: utilityInfo, loading: utilityLoading } = useUtilityDetail(project.utility_id);
  const unlistedUtility = !project.utility_id && (project.ami_data_provider ?? '').trim().length > 0;
  const routesKnown = project.utility_id ? !utilityLoading && utilityInfo != null : unlistedUtility;
  const routes = routesFor(project.utility_id ? utilityInfo : null);
  const utilityName = (utilityInfo?.name ?? project.ami_data_provider ?? '').trim();

  // An explicit path choice sets the report basis when the other path holds
  // nothing — so validation and the progress strip follow the contractor's
  // actual path immediately. When both paths hold data, the Results tab's
  // basis picker owns the decision. A stored basis whose path cannot run is
  // no decision at all: switching to a path that can run replaces it.
  const chooseMode = (m: Exclude<Mode, null>) => {
    props.onModeChange(m);
    const stored = project.report_method;
    if (isReadOnly || stored === m) return;
    const measuredRuns = canRunMeasured(project, props.amiData);
    const traditionalRuns = canRunTraditional(project);
    if (stored && (stored === 'measured' ? measuredRuns : traditionalRuns)) return;
    const chosenRuns = m === 'measured' ? measuredRuns : traditionalRuns;
    const otherHasData =
      m === 'traditional'
        ? amiDataCount > 0 || (project.max_recorded_demand_kw ?? 0) > 0
        : project.dwelling_floor_area_sqft != null || existingRows.length > 0;
    if (!otherHasData || (stored && chosenRuns)) {
      onChange({
        report_method: m,
        methodology_version:
          m === 'measured' ? measuredMethodologyVersion(project) : traditionalMethodologyVersion(project),
      });
    }
  };

  // Abandoning the traditional path: its inputs and dwelling inventory go,
  // and the report basis falls back to measured when measured can run.
  // Removed-equipment rows stay — the measured path lists and credits them.
  const clearTraditional = () => {
    if (
      !window.confirm(
        'Clear the traditional calculation? This removes the floor area, occupancy answer, attestation and every dwelling-inventory item on this project. Measured data and the New Loads list are not touched.'
      )
    ) {
      return;
    }
    existingRows.forEach((row) => onDeleteEquipment(row.id));
    const measuredRuns = canRunMeasured(project, props.amiData);
    onChange({
      ...TRADITIONAL_INPUT_RESET,
      ...(measuredRuns
        ? { report_method: 'measured', methodology_version: measuredMethodologyVersion(project) }
        : project.report_method === 'traditional'
          ? { report_method: null }
          : {}),
    });
    props.onModeChange('measured');
  };

  // A link-based route exists: the customer's own download or an email
  // request. Either one makes measured demand the recommended path.
  const amiSupported = routesKnown && routes.primary !== null;

  // Scope eligibility for the traditional path (calc/eligibility.ts). An
  // 'occupancy-unknown' verdict just means the question hasn't been answered
  // — the picker below asks it. Any other verdict disables the path with the
  // measured method named as the way forward. The measured path never sees
  // any of this.
  const gate = checkTraditionalEligibility(project);
  const hardIneligible = !gate.eligible && gate.reason !== 'occupancy-unknown';

  const occupancyPicker = (
    <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4 sm:p-6" data-testid="occupancy-picker">
      <p className="text-sm font-medium text-gray-800 mb-2">What does this service feed?</p>
      <div className="space-y-1.5">
        {OCCUPANCY_OPTIONS.map((opt) => (
          <label key={opt.value} className="flex items-center gap-2.5 text-sm text-gray-800 cursor-pointer">
            <input
              type="radio"
              name="occupancy-type"
              value={opt.value}
              checked={project.occupancy_type === opt.value}
              onChange={() => onChange({ occupancy_type: opt.value })}
              disabled={isReadOnly}
              data-testid={`occupancy-${opt.value}`}
              className="w-4 h-4 text-blue-600 focus:ring-2 focus:ring-blue-500 disabled:opacity-50"
            />
            <span>{opt.label}</span>
            {opt.tooltip && <Tooltip text={opt.tooltip} />}
          </label>
        ))}
      </div>
    </div>
  );

  const ineligibleNotice = !gate.eligible && (
    <div
      data-testid="traditional-ineligible"
      className="flex items-start gap-3 rounded-lg border border-amber-200 bg-amber-50 p-4"
    >
      <AlertCircle className="w-5 h-5 text-amber-600 flex-shrink-0 mt-0.5" />
      <div className="text-sm text-amber-900">
        <p className="font-semibold">
          {gate.reason === 'occupancy-unknown'
            ? 'One question first'
            : 'The traditional calculation does not apply here'}
        </p>
        <p className="mt-1 leading-relaxed">{gate.message}</p>
        <p className="mt-1 leading-relaxed">{gate.alternative}</p>
        {gate.reason !== 'occupancy-unknown' && !isReadOnly && (
          <button
            onClick={() => chooseMode('measured')}
            data-testid="btn-switch-measured"
            className="mt-2 text-xs font-semibold text-blue-700 underline hover:text-blue-900"
          >
            Use measured demand
          </button>
        )}
      </div>
    </div>
  );

  // Short on purpose. Everything about how the utility shares its data lives
  // on the Get data page (Step 1 of the measured path), not on this tab.
  // One line. The cards below carry the case for each path; this only says
  // what the utility offers.
  const banner = (() => {
    if (routesKnown && routes.primary === 'self_download') {
      return {
        tone: 'blue' as const,
        text: routes.unlisted ? (
          <>Most smart-meter portals let the customer download their own interval data in a few minutes.</>
        ) : (
          <><strong>{utilityName}</strong> customers can download their own interval data in about five minutes.</>
        ),
      };
    }
    if (routesKnown && routes.primary === 'customer_request') {
      return {
        tone: 'blue' as const,
        text: isUsableTemplate(utilityInfo?.request_form_template)
          ? <><strong>{utilityName}</strong> takes interval-data requests from the customer on its website form. TrueLoad fills it in for them.</>
          : <><strong>{utilityName}</strong> takes interval-data requests from the customer. TrueLoad writes the request for them.</>,
      };
    }
    if (routesKnown && routes.primary === 'email_request') {
      return {
        tone: 'blue' as const,
        text: <><strong>{utilityName}</strong> provides interval data by email request, usually 2–5 business days.</>,
      };
    }
    if (routesKnown) {
      return {
        tone: 'amber' as const,
        text: <>No interval-data route is known for <strong>{utilityName}</strong> yet. The traditional calculation works for every home.</>,
      };
    }
    return {
      tone: 'gray' as const,
      text: <>Select the utility on the Service Info tab and TrueLoad will recommend a path.</>,
    };
  })();

  const bannerClass =
    banner.tone === 'blue'
      ? 'bg-blue-50 border-blue-200 text-blue-900'
      : banner.tone === 'amber'
        ? 'bg-amber-50 border-amber-200 text-amber-900'
        : 'bg-gray-50 border-gray-200 text-gray-700';

  const cardBase =
    'flex-1 text-left border rounded-lg p-4 sm:p-5 transition-colors focus:outline-none focus:ring-2 focus:ring-blue-500';

  return (
    <div data-testid="existing-load-step" className="space-y-4">
      {/* The edition governs both paths: it selects the traditional engine's
          parameters AND the section numbers every report prints, measured
          included. One control, above the fork, so the two cannot disagree.
          Choosing the state's own default stores nothing, so the edition
          keeps following the state (and any later correction of it) until
          the preparer picks something else. */}
      <div className="flex flex-wrap items-center justify-end gap-2">
        <label htmlFor="code-edition" className="text-xs font-medium text-gray-600">
          Code edition
        </label>
        <select
          id="code-edition"
          value={project.code_edition ?? stateDefault}
          onChange={(e) =>
            onChange({
              code_edition: editionSelectionToStore(e.target.value as EditionKey, stateCode),
            })
          }
          data-testid="code-edition"
          className="text-xs border border-gray-300 rounded-md px-2 py-1.5 bg-white text-gray-800 focus:outline-none focus:ring-2 focus:ring-blue-500"
          disabled={isReadOnly}
        >
          {(Object.keys(EDITION_PACKS) as EditionKey[]).map((key) => (
            <option key={key} value={key}>
              NEC {key}
              {key === '2026' ? ' (Article 120)' : ''}
              {key === stateDefault ? (stateCode ? ` · default for ${stateCode}` : ' · default') : ''}
            </option>
          ))}
        </select>
        <Tooltip text="The NEC edition your jurisdiction enforces. Follows the project's state until you choose one here; the AHJ is the final word. Section numbers on the report follow this choice, on both calculation paths." />
      </div>

      {/* The recommendation has done its job once measured data is loaded. */}
      {!(mode === 'measured' && amiDataCount > 0) && (
        <div className={`border rounded-md p-3 sm:p-4 text-xs sm:text-sm ${bannerClass}`} data-testid="path-recommendation">
          {banner.text}
        </div>
      )}

      {mode === null ? (
        <div className="flex flex-col sm:flex-row gap-4">
          <button
            onClick={() => chooseMode('measured')}
            data-testid="card-measured"
            className={`${cardBase} bg-white border-gray-200 hover:border-blue-400`}
          >
            <div className="flex items-center gap-2 mb-2">
              <Activity size={20} className="text-blue-600" />
              <span className="font-semibold text-gray-900">Measured demand</span>
              <span className="text-xs text-gray-500">NEC {measuredRef}</span>
              {amiSupported && (
                <span className="text-xs px-2 py-0.5 rounded-full bg-blue-100 text-blue-800 font-medium">
                  Recommended
                </span>
              )}
            </div>
            <p className="text-sm text-gray-600">
              Uses the home&apos;s own utility data — the strongest number available, on
              average about 40% lower than a traditional calculation. Needs 12 months of
              interval data (or the 30-day exception).
            </p>
          </button>

          {hardIneligible ? (
            <div
              data-testid="card-traditional-disabled"
              className={`${cardBase} bg-gray-50 border-gray-200 opacity-90 cursor-not-allowed`}
            >
              <div className="flex items-center gap-2 mb-2">
                <Home size={20} className="text-gray-400" />
                <span className="font-semibold text-gray-500">Traditional calculation</span>
                <span className="text-xs px-2 py-0.5 rounded-full bg-gray-200 text-gray-600 font-medium">
                  Not applicable
                </span>
              </div>
              {!gate.eligible && (
                <p className="text-sm text-gray-600">{gate.message}</p>
              )}
              {!isReadOnly && (
                <button
                  onClick={() => chooseMode('measured')}
                  data-testid="btn-card-switch-measured"
                  className="mt-2 text-xs font-semibold text-blue-700 underline hover:text-blue-900"
                >
                  Use measured demand
                </button>
              )}
            </div>
          ) : (
          <button
            onClick={() => chooseMode('traditional')}
            data-testid="card-traditional"
            className={`${cardBase} bg-white border-gray-200 hover:border-blue-400`}
          >
            <div className="flex items-center gap-2 mb-2">
              <Home size={20} className="text-blue-600" />
              <span className="font-semibold text-gray-900">Traditional calculation</span>
              <span className="text-xs text-gray-500">NEC {traditionalRef}</span>
              {routesKnown && !amiSupported && (
                <span className="text-xs px-2 py-0.5 rounded-full bg-blue-100 text-blue-800 font-medium">
                  Recommended
                </span>
              )}
            </div>
            <p className="text-sm text-gray-600">
              The code&apos;s calculation method, built from the home&apos;s appliances and
              floor area. No utility data, no waiting — works for every home, done in
              minutes.
            </p>
          </button>
          )}
        </div>
      ) : (
        <div
          className="flex rounded-lg border border-gray-300 overflow-hidden shadow-sm"
          data-testid="mode-switch"
          role="tablist"
          aria-label="Existing load method"
        >
          <button
            onClick={() => chooseMode('measured')}
            data-testid="btn-mode-measured"
            role="tab"
            aria-selected={mode === 'measured'}
            className={`flex-1 flex items-center justify-center gap-2 px-4 py-3 text-sm font-semibold border-r border-gray-300 transition-colors ${
              mode === 'measured'
                ? 'bg-blue-600 text-white'
                : 'bg-white text-gray-600 hover:bg-gray-50'
            }`}
          >
            <Activity size={16} className="flex-shrink-0" />
            <span>Measured demand</span>
            <span
              className={`hidden sm:inline text-xs font-normal ${
                mode === 'measured' ? 'text-blue-100' : 'text-gray-400'
              }`}
            >
              {amiDataCount > 0 ? 'Data loaded' : 'Utility interval data'}
            </span>
          </button>
          <button
            onClick={() => chooseMode('traditional')}
            data-testid="btn-mode-traditional"
            role="tab"
            aria-selected={mode === 'traditional'}
            className={`flex-1 flex items-center justify-center gap-2 px-4 py-3 text-sm font-semibold transition-colors ${
              mode === 'traditional'
                ? 'bg-blue-600 text-white'
                : 'bg-white text-gray-600 hover:bg-gray-50'
            }`}
          >
            <Home size={16} className="flex-shrink-0" />
            <span>Traditional calculation</span>
            <span
              className={`hidden sm:inline text-xs font-normal ${
                mode === 'traditional' ? 'text-blue-100' : 'text-gray-400'
              }`}
            >
              Dwelling profile
            </span>
          </button>
        </div>
      )}

      {mode === 'measured' &&
        !isDemoProjectId(projectId) &&
        amiDataCount === 0 &&
        !((project.max_recorded_demand_kw ?? 0) > 0) && (
          <p className="text-xs text-gray-500" data-testid="measured-waiting-nudge">
            Waiting on utility data? The traditional calculation can finish this job today —
            switch above. The data request stays open either way.
          </p>
        )}

      {mode === 'measured' && (
        <AMIDataInput
          projectId={props.projectId}
          project={props.project}
          amiDataCount={props.amiDataCount}
          amiData={props.amiData}
          onChange={props.onChange}
          onUploadCSV={props.onUploadCSV}
          onDeleteData={props.onDeleteData}
          isReadOnly={props.isReadOnly}
          showRequiredHighlights={props.showRequiredHighlights}
          subscriptionStatus={props.subscriptionStatus}
          unlocked={props.unlocked}
          onRelockTriggered={props.onRelockTriggered}
          onReloadProject={props.onReloadProject}
          onNavigate={props.onNavigate}
        />
      )}

      {mode === 'traditional' && (
        <>
          {!isReadOnly && hasTraditionalEntries(project, existingRows.length) && (
            <div className="flex justify-end">
              <button
                onClick={clearTraditional}
                data-testid="btn-clear-traditional"
                className="text-xs font-medium text-gray-600 underline hover:text-red-700"
              >
                Not using this method? Clear the traditional calculation
              </button>
            </div>
          )}
          {occupancyPicker}
          {hardIneligible && ineligibleNotice}
          {gate.eligible && (
            <DwellingProfile
              project={project}
              existingRows={existingRows}
              onChange={onChange}
              onAddExisting={onAddExisting}
              onSplitExisting={onSplitExisting}
              onMarkRemoved={onMarkRemoved}
              removedRows={removedLoads(equipment)}
              onReturnToInventory={onReturnToInventory}
              onUpdateEquipment={onUpdateEquipment}
              onDeleteEquipment={onDeleteEquipment}
              isReadOnly={isReadOnly}
            />
          )}
        </>
      )}
    </div>
  );
}
